import {PaperClipIcon, SearchIcon, SendIcon, TrashIcon, XIcon} from "assets/icons";
import Loader from "components/loaders/Loader";
import firebase from "config/firebase";
import useAuthContext from "contexts/Auth/useAuthContext";
import useChat from "contexts/Chat/useChat";
import useChats from "contexts/Chat/useChats";
import useUser from "contexts/User/useUser";
import {getDownloadURL, ref} from "firebase/storage";
import useAwaitableModal from "hooks/useAwaitableModal";
import moment from "moment";
import {FC, Fragment, RefObject, useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import SearchMessagesModal from "./modals/Search";
import {DefaultProfileSrc} from "assets/images";
import ProfileImage from "components/ProfileImage";
import Modal from "components/Modal";
import chatsApi from "api/chats";
import {handleError} from "helpers/errors";

interface Props {
  chatId: string;
}

const useScroll = (scrollRef: RefObject<HTMLDivElement>, size: number, deps?: any) => {
  const [lastHeight, setLastHeight] = useState<number>(0);

  useEffect(() => {
    if (!scrollRef.current) return;
    const {scrollTop, scrollHeight} = scrollRef.current;

    if (!lastHeight) {
      scrollRef.current.scrollTop = scrollHeight;
    } else {
      if (scrollTop === 0) {
        const diff = scrollHeight - lastHeight;
        scrollRef.current.scrollTop = diff;
      }
    }
  }, [size, lastHeight, scrollRef, deps]);

  useEffect(() => {
    setLastHeight(scrollRef.current?.scrollHeight || 0);
  }, [scrollRef]);

  const prepareLoad = useCallback(() => {
    if (!scrollRef.current || !size) return;
    const {scrollHeight} = scrollRef.current;
    setLastHeight(scrollHeight);
  }, [scrollRef, size]);

  return {prepareLoad};
};

const Chat: FC<Props> = ({chatId}) => {
  const user = useAuthContext();
  const isAccountant = useUser().type === "accountant";
  const me = user?.uid!;
  const {threads} = useChats();
  const hasUnread = !!threads.find((thread) => thread.id === chatId)?.unread;
  const {
    chat,
    setSearchBeginning,
    messages: unsortedMessages,
    concept,
    setConcept,
    sendConcept,
    sending,
    loadNext,
    loadingNextAfter,
    loadingNextBefore,
  } = useChat(chatId);
  const messages = unsortedMessages
    ?.slice(0)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const fileInput = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledTo = useRef<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const onDelete = () => {
    if (isDeleting) return;
    setIsDeleting(true);
    chatsApi
      .deleteChat(chatId)
      .then(() => {
        setIsDeleting(false);
        setConfirmDelete(false);
        handleError("Chat deleted successfully");
      })
      .catch(() => {
        setIsDeleting(false);
      });
  };

  const {prepareLoad} = useScroll(scrollRef, messages?.length || 0);

  const [search, SearchMessagesComponent] = useAwaitableModal(SearchMessagesModal, {});

  const [searchedMessage, setSearchedMessage] = useState<string>();

  const onSearch = () => {
    search()
      .then((res) => {
        if (!res) return;
        prepareLoad();
        if (messages?.find((message) => message.id === res.selected?.id)) return setSearchedMessage(res.selected?.id);
        setSearchBeginning(res.selected!);
        setSearchedMessage(res.selected!.id);
      })
      .catch(() => null);
  };

  const otherPerson = chat?.members?.find((id) => id !== me);
  const lastActiveAt = chat?.lastSeen?.[otherPerson!];

  const isOnline = lastActiveAt ? new Date(lastActiveAt).getTime() > Date.now() - 5 * 60 * 1000 : undefined;

  useEffect(() => {
    if (!scrollRef.current) return;

    const isScrollTop = () => {
      if (!scrollRef.current) return false;
      return scrollRef.current.scrollTop < 50;
    };

    const isScrollBottom = () => {
      const e = scrollRef.current;
      if (!e) return false;
      return e.scrollHeight - e.scrollTop - e.clientHeight < 50;
    };

    function handleChatScroll() {
      prepareLoad();
      if (isScrollTop()) loadNext("before");
      if (isScrollBottom()) loadNext("after");
    }

    handleChatScroll();
    window.addEventListener("scroll", handleChatScroll, true);
    return () => window.removeEventListener("scroll", handleChatScroll, true);
  }, [loadNext, prepareLoad, scrollRef]);

  const navigate = useNavigate();

  useEffect(() => {
    if (hasUnread) {
      prepareLoad();
      loadNext("after", true);
    }
  }, [hasUnread, loadNext, prepareLoad]);

  const goToProfile = () => {
    const otherUser = chat?.members.find((id) => id !== me);
    if (!otherUser) return;
    if (isAccountant) {
      navigate("/recruiter/" + otherUser);
    } else {
      navigate("/accountant/" + otherUser);
    }
  };

  const removeFile = () => {
    setConcept((old) => ({...old, file: undefined}));
    fileInput.current?.value && (fileInput.current.value = "");
    fileInput.current?.files && (fileInput.current.files = null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendConcept(chatId);
    return false;
  };

  const downloadFile = async (file: string, _name: string) => {
    const fileRef = ref(firebase.storage, file);
    const url = await getDownloadURL(fileRef);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!chat || chat.id !== chatId || messages?.some((m) => m.chatId !== chatId))
    return (
      <div className="flex grow flex-col overflow-hidden h-full items-center justify-center bg-white rounded-xl border border-brand-border">
        <Loader />
      </div>
    );

  return (
    <div className="flex grow flex-col overflow-hidden h-full bg-white rounded-xl border border-brand-border">
      {SearchMessagesComponent}
      <Modal open={confirmDelete} close={() => setConfirmDelete(false)}>
        <div className="flex flex-col gap-4 p-5 bg-white rounded-2xl border border-brand-border">
          <span className="text-sm font-medium text-brand-navy">Are you sure you want to delete this chat?</span>
          <div className="flex gap-3">
            <button onClick={() => setConfirmDelete(false)} disabled={isDeleting} className="btn-secondary grow">
              Cancel
            </button>
            <button onClick={() => onDelete()} disabled={isDeleting} className="btn-primary grow">
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <div className="flex flex-col grow overflow-hidden">
        <div className="flex items-center gap-4 px-5 py-4 border-b border-brand-border">
          <div className="flex items-center gap-4 group cursor-pointer grow" onClick={goToProfile}>
            <ProfileImage
              src={chat?.otherProfiles[0].image || DefaultProfileSrc}
              size={56}
              className="w-14 h-14 rounded-full object-cover"
            />
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-base text-brand-navy group-hover:underline">
                {chat?.otherProfiles[0].name}
              </span>
              <span className="text-xs text-brand-muted">
                {isOnline
                  ? "Online"
                  : lastActiveAt
                    ? `Last seen ${moment(new Date(lastActiveAt)).locale("en-US").fromNow()}`
                    : ""}
              </span>
            </div>
          </div>
          <button
            onClick={onSearch}
            className="p-2 rounded-xl border border-brand-border bg-white hover:border-brand-amber transition-colors group"
          >
            <SearchIcon className="w-5 h-5 text-brand-muted group-hover:text-brand-amber transition-colors" />
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            className="p-2 rounded-xl border border-brand-border bg-white hover:border-red-300 transition-colors group"
          >
            <TrashIcon className="w-5 h-5 text-brand-muted group-hover:text-red-400 transition-colors" />
          </button>
        </div>
        <div ref={scrollRef} className="grow h-full overflow-y-auto">
          <div className="flex flex-col justify-end min-h-full gap-4 items-center w-full px-8 py-4 relative">
            {loadingNextBefore && (
              <div className="flex justify-center items-center w-full py-5 mt-9 top-0 absolute">
                <Loader />
              </div>
            )}
            {!messages && (
              <div className="flex justify-center items-center h-full">
                <Loader />
              </div>
            )}
            {messages?.length === 0 && (
              <div className="flex justify-center items-center h-full">
                <span className="text-sm text-brand-muted">No messages yet</span>
              </div>
            )}
            {messages?.map((message, i) => {
              const isMe = message.author === me;
              const authorName = isMe ? "You" : chat?.otherProfiles[0].name;
              const previousMessage = messages[i - 1];
              const isToday = new Date(message.createdAt).getDate() === new Date().getDate();
              const isDifferentDay =
                previousMessage &&
                new Date(message.createdAt).getDate() !== new Date(previousMessage.createdAt).getDate();
              const showDate = !previousMessage || isDifferentDay;
              const hasFile = !!message.file;
              const fileName = hasFile ? message.file!.name : undefined;

              return (
                <Fragment key={message.id}>
                  {showDate && (
                    <span className="text-xs text-brand-muted text-center bg-brand-cream rounded-full px-3 py-1 w-max self-center mb-2">
                      {isToday ? "Today" : new Date(message.createdAt).toDateString()}
                    </span>
                  )}
                  <div
                    ref={(el) => {
                      if (message.id === searchedMessage) {
                        if (el && scrolledTo.current !== message.id) {
                          el.scrollIntoView({behavior: "auto", block: "end"});
                          scrolledTo.current = message.id;
                        }
                      }
                    }}
                    className={
                      ["flex flex-col gap-1.5 group", !isMe ? "items-start self-start" : "items-end self-end"].asClass
                    }
                    style={{maxWidth: "calc(100% - 4rem)"}}
                  >
                    {!isMe && <span className="text-xs text-brand-muted font-medium px-1">{authorName}</span>}
                    <span
                      className="text-sm py-2.5 px-4 rounded-2xl relative z-10 break-words"
                      style={{
                        background: isMe ? "#282D4D" : "#F5F3F0",
                        borderTopLeftRadius: isMe ? undefined : "4px",
                        borderBottomRightRadius: isMe ? "4px" : undefined,
                        color: isMe ? "#fff" : "#282D4D",
                        maxWidth: "100%",
                      }}
                    >
                      {message.content}
                      {hasFile && (
                        <>
                          {!!message.content.trim() && <br />}
                          <button
                            onClick={() => downloadFile(message.file?.ref!, fileName!)}
                            className="hover:underline cursor-pointer bg-white/20 text-inherit rounded px-1.5 py-0.5 text-xs"
                          >
                            {fileName}
                          </button>
                        </>
                      )}
                    </span>
                    <span className="text-xs text-brand-muted transition-all group-hover:translate-y-0 -translate-y-full opacity-0 group-hover:opacity-100 px-1">
                      {new Date(message.createdAt)
                        .toLocaleTimeString("en-US", {hour: "numeric", minute: "numeric", hour12: true})
                        .toLowerCase()
                        .replace(" ", "")}
                    </span>
                  </div>
                </Fragment>
              );
            })}
            {loadingNextAfter && (
              <div className="flex justify-center items-center w-full py-5 absolute bottom-0">
                <Loader />
              </div>
            )}
          </div>
        </div>
      </div>
      <form
        onSubmit={handleSubmit}
        className="flex items-start gap-2.5 p-4 px-5 border-t border-brand-border"
        style={{pointerEvents: sending ? "none" : "auto"}}
      >
        <div className="flex flex-col grow">
          <div className="relative grow">
            <input
              type="text"
              value={concept.content}
              disabled={sending}
              onChange={(e) => setConcept((old) => ({...old, content: e.target.value}))}
              placeholder="Type something here..."
              className="w-full !pl-5 !pr-12 !rounded-2xl !border !py-3 !border-brand-border bg-brand-cream !outline-none"
            />
            <input
              id="file"
              className="hidden"
              ref={fileInput}
              type="file"
              onChange={(e) => setConcept((old) => ({...old, file: e.target.files?.[0]}))}
            />
            <label htmlFor="file" className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <PaperClipIcon className="w-8 h-8 text-brand-muted cursor-pointer hover:text-brand-navy rounded-lg transition-colors p-1.5" />
            </label>
          </div>
          {concept.file && (
            <div className="flex items-center gap-2.5 mt-2 p-2 rounded-xl border border-brand-border w-max">
              <span className="text-xs text-brand-muted">{concept.file.name}</span>
              <XIcon
                onClick={removeFile}
                className="w-4 h-4 text-brand-muted cursor-pointer hover:text-brand-navy rounded transition-colors"
              />
            </div>
          )}
        </div>
        <button type="submit" className="btn-primary !px-3 !py-3">
          <SendIcon className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};

export default Chat;
